/**
 * stuburt-quick-pick.js — card quick-add that matches the homepage carousel.
 *
 * A circular cart-icon button on the product card.
 *  - Single variant → adds straight to cart.
 *  - One option (size only, or colour only) → reveals chips; a tap adds instantly.
 *  - Two options (colour + size) → reveals both rows with NOTHING preselected; the
 *    customer picks a colour and a size, then commits with an explicit Add button.
 * No full-screen quick-add modal.
 *
 * Markup + the colour→size variant matrix: snippets/stuburt-card-quick-add.liquid.
 * Adds via /cart/add.js and dispatches the `cart:update` event the branded
 * mini-cart + Horizon's cart bubble subscribe to — the same contract used by
 * sections/carousel-p.liquid on the homepage.
 *
 * Non-negotiables: Custom Element (no framework), full teardown in
 * disconnectedCallback (the editor re-renders aggressively), prefers-reduced-
 * motion handled in CSS. No prices/money in JS — Liquid renders those.
 */

// Ceiling for the quick-add stepper. Deliberately low: this is an impulse
// control on a card, not the cart page, and someone buying eleven of one polo
// wants the real quantity field with its stock rules, not a popup.
const QTY_MAX = 10;

class StuburtQuickPick extends HTMLElement {
  #abort = null;
  #open = false;
  #busy = false;
  /** @type {Array<{id:number, v:string, f:string, a:boolean}>} */
  #matrix = [];
  #hasFilter = false;
  #selFilter = '';
  #selSize = '';
  /** Backdrop element while the picker is shown as a mobile bottom sheet. */
  #backdrop = null;
  /** True while the panel is portaled onto <body> (sheet OR floating popover). */
  #portaled = false;
  /** Idle timer — closes the desktop popover 3s after the pointer leaves both
      the card and the panel. */
  #autoCloseTimer = null;
  /** Minimum quantity, captured once at init. NOT read from the input's
      defaultValue: for <input type="hidden"> the value IDL attribute reflects
      the content attribute, so defaultValue tracks value and the floor would
      drift upward with every step — which disabled the minus button for good. */
  #qtyBase = 1;

  connectedCallback() {
    this.trigger = this.querySelector('[data-quick-pick-trigger]');
    this.panel = this.querySelector('[data-quick-pick-panel]');
    this.addBtn = this.querySelector('[data-quick-pick-add]');
    this.multi = this.dataset.mode === 'choose';
    if (!this.trigger) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.trigger.addEventListener('click', this.#onTrigger, { signal });

    if (this.multi) {
      const raw = this.querySelector('[data-quick-pick-variants]')?.textContent;
      try {
        this.#matrix = raw ? JSON.parse(raw) : [];
      } catch {
        this.#matrix = [];
      }
      // Two-dimension product (colour + size) → deliberate select-then-add.
      // Query the chips through this.panel (not this) — the panel is portaled
      // out to <body> when shown as a mobile bottom sheet, so it stays the
      // stable root for every chip lookup wherever it currently lives.
      this.#hasFilter = !!this.panel?.querySelector('[data-quick-pick-filter]');

      for (const chip of this.panel?.querySelectorAll('[data-quick-pick-filter]') ?? []) {
        chip.addEventListener('click', this.#onFilter, { signal });
      }
      for (const chip of this.panel?.querySelectorAll('[data-quick-pick-size]') ?? []) {
        chip.addEventListener('click', this.#onSize, { signal });
      }
      if (this.addBtn) this.addBtn.addEventListener('click', this.#onAdd, { signal });

      // Quantity stepper. It only ever writes the hidden [data-quick-pick-qty]
      // input, which the add path already reads — so adding stays untouched.
      // Looked up through this.panel, not this, because the panel portals out to
      // <body> and these controls travel with it.
      this.#qtyBase = Math.max(1, parseInt(this.querySelector('[data-quick-pick-qty]')?.value ?? '1', 10) || 1);
      for (const btn of this.panel?.querySelectorAll('[data-quick-pick-qty-step]') ?? []) {
        btn.addEventListener('click', this.#onQtyStep, { signal });
      }
      this.#syncQty();

      // Idle auto-close — schedule a close when the pointer leaves the card OR
      // the (portaled) panel, cancel it when it re-enters either. Desktop only;
      // the schedule itself bails on touch (#scheduleAutoClose checks #isSheet).
      const tile = this.closest('product-card') ?? this;
      tile.addEventListener('pointerenter', this.#cancelAutoClose, { signal });
      tile.addEventListener('pointerleave', this.#scheduleAutoClose, { signal });
      this.panel?.addEventListener('pointerenter', this.#cancelAutoClose, { signal });
      this.panel?.addEventListener('pointerleave', this.#scheduleAutoClose, { signal });

      document.addEventListener('click', this.#onOutside, { signal });
      // On document, not this — the panel is portaled to <body> as a sheet, so
      // focus (and its keydown) can sit outside this element while it's open.
      document.addEventListener('keydown', this.#onKeydown, { signal });
      // Close on scroll, and when another card's picker opens.
      window.addEventListener('scroll', this.#onScroll, { signal, passive: true });
      window.addEventListener('resize', this.#onResize, { signal });
      document.addEventListener('stuburt-quick-pick:open', this.#onOtherOpen, { signal });

      this.#reset();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#open = false;
    this.#busy = false;
    clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = null;
    // If torn down (editor re-render) while portaled — sheet OR floating popover —
    // the panel (and a sheet's backdrop) live on <body>; remove them so nothing is
    // orphaned, and lift any scroll lock.
    if (this.#backdrop) {
      this.#backdrop.remove();
      this.#backdrop = null;
    }
    if (this.panel && this.panel.parentElement === document.body) this.panel.remove();
    document.body.style.overflow = '';
    this.#portaled = false;
  }

  /**
   * Two dimensions (colour + size) → pre-select the default (base) variant so the
   * Add button is ready. Single dimension → nothing selected (it stays tap-to-add).
   */
  #reset() {
    this.#selFilter = this.#hasFilter ? this.dataset.baseFilter ?? '' : '';
    this.#selSize = this.#hasFilter ? this.dataset.baseSize ?? '' : '';
    for (const chip of this.panel?.querySelectorAll('[data-quick-pick-filter]') ?? []) {
      chip.setAttribute('aria-pressed', String(chip.dataset.val === this.#selFilter));
    }
    for (const chip of this.panel?.querySelectorAll('[data-quick-pick-size]') ?? []) {
      chip.setAttribute('aria-pressed', String(this.#hasFilter && chip.dataset.val === this.#selSize));
    }
    this.#refresh();
  }

  /** Scope the size chips to the chosen colour (or full run if none chosen yet). */
  #refresh() {
    for (const chip of this.panel?.querySelectorAll('[data-quick-pick-size]') ?? []) {
      const v = chip.dataset.val;
      let ok;
      if (this.#hasFilter && this.#selFilter) {
        const m = this.#matrix.find((x) => x.v === v && x.f === this.#selFilter);
        ok = !!(m && m.a);
      } else if (this.#hasFilter) {
        // No colour chosen yet — show the full run; available if ANY colour has it.
        ok = this.#matrix.some((x) => x.v === v && x.a);
      } else {
        const m = this.#matrix.find((x) => x.v === v && x.f === '');
        ok = !!(m && m.a);
      }
      chip.disabled = !ok;
      chip.setAttribute('aria-disabled', String(!ok));
      // Drop a selection that the new colour no longer offers.
      if (!ok && chip.getAttribute('aria-pressed') === 'true') {
        chip.setAttribute('aria-pressed', 'false');
        if (this.#selSize === v) this.#selSize = '';
      }
    }
    this.#updateAdd();
  }

  #updateAdd() {
    if (!this.addBtn) return;
    const ready = (!this.#hasFilter || this.#selFilter) && this.#selSize;
    this.addBtn.disabled = !ready;
  }

  #onFilter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const chip = event.currentTarget;
    if (chip.disabled) return;
    this.#selFilter = chip.dataset.val ?? '';
    for (const c of this.panel?.querySelectorAll('[data-quick-pick-filter]') ?? []) {
      c.setAttribute('aria-pressed', String(c === chip));
    }
    this.#refresh();
  };

  #onSize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const chip = event.currentTarget;
    if (chip.disabled) return;

    if (this.#hasFilter) {
      // Two dimensions — select only; the Add button commits.
      this.#selSize = chip.dataset.val ?? '';
      for (const c of this.panel?.querySelectorAll('[data-quick-pick-size]') ?? []) {
        c.setAttribute('aria-pressed', String(c === chip));
      }
      this.#updateAdd();
    } else {
      // Single dimension — keep the fast tap-to-add.
      const m = this.#matrix.find((x) => x.v === chip.dataset.val && x.f === '');
      if (m && m.a) {
        this.#toggle(false);
        this.#add(String(m.id));
      }
    }
  };

  #onAdd = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#busy) return;
    const filter = this.#hasFilter ? this.#selFilter : '';
    const m = this.#matrix.find((x) => x.v === this.#selSize && x.f === filter);
    if (!m || !m.a) return;
    this.#toggle(false);
    this.#add(String(m.id));
  };

  #onTrigger = (event) => {
    // Never navigate the card link or submit anything — we own the add.
    event.preventDefault();
    event.stopPropagation();
    if (this.#busy) return;
    if (this.multi) {
      const next = !this.#open;
      if (next) this.#reset();
      this.#toggle(next);
    } else {
      const input = this.querySelector('[data-quick-pick-id]');
      this.#add(input?.value);
    }
  };

  #onOutside = (event) => {
    // The panel may be portaled to <body> as a sheet, so check it too — only a
    // click truly outside both the control and the panel closes it (the sheet
    // backdrop has its own close handler).
    if (
      this.#open &&
      !this.contains(event.target) &&
      !(this.panel && this.panel.contains(event.target))
    ) {
      this.#toggle(false);
    }
  };

  // Pointer rested off both the card and the panel — close after 3s (desktop
  // popover only; the mobile sheet is dismissed via its backdrop).
  #scheduleAutoClose = () => {
    if (!this.#open || this.#isSheet()) return;
    clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = setTimeout(() => {
      this.#autoCloseTimer = null;
      if (this.#open) this.#toggle(false);
    }, 3000);
  };

  // Pointer back on the card or panel — keep it open.
  #cancelAutoClose = () => {
    clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = null;
  };

  #onScroll = () => {
    // Desktop popover closes on scroll; the mobile sheet doesn't (the page is
    // scroll-locked behind its backdrop while open).
    if (this.#open && !this.#backdrop) this.#toggle(false);
  };

  #onResize = () => {
    // Keep the floating popover pinned to its trigger across viewport changes.
    if (this.#open && this.panel?.classList.contains('is-floating')) this.#position();
  };

  #onOtherOpen = (event) => {
    if (event.detail !== this && this.#open) this.#toggle(false);
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#open) {
      this.#toggle(false);
      this.trigger.focus();
    }
  };

  #toggle(next) {
    clearTimeout(this.#autoCloseTimer);
    this.#autoCloseTimer = null;
    this.#open = next;
    if (next) {
      // Tell every other picker to close FIRST (its #onTrigger stops propagation,
      // so a plain document click wouldn't reach them) — before we mount + lock
      // body scroll, so a sibling's teardown can't unlock it back underneath us.
      document.dispatchEvent(new CustomEvent('stuburt-quick-pick:open', { detail: this }));
      // Portal the panel onto <body> either way — a bottom sheet on touch, a
      // floating popover on desktop — so it escapes the card-gallery's overflow
      // clip entirely and can extend over whatever surrounds the card.
      if (this.#isSheet()) this.#mountSheet();
      else this.#mountFloating();
      // Clear any half-finished exit so re-opening mid-close starts clean.
      this.panel?.classList.remove('is-closing');
      this.#backdrop?.classList.remove('is-closing');
      if (this.panel) this.panel.hidden = false;
      // Fresh count every time it opens — reopening after adding three should
      // not silently offer another three.
      const qtyInput = this.querySelector('[data-quick-pick-qty]');
      if (qtyInput) qtyInput.value = String(this.#qtyBase);
      this.#syncQty();
    } else if (this.panel && !this.panel.hidden) {
      // Animate out before hiding. Setting `hidden` (and unmounting) straight
      // away is why the sheet rose smoothly and then simply vanished — the exit
      // keyframes never got a frame to run in. #playClose does both once done.
      this.#playClose();
    } else {
      this.#unmount();
    }
    this.trigger.setAttribute('aria-expanded', String(next));
    this.classList.toggle('is-open', next);
    if (next) {
      // Anchor the popover now it's visible (a hidden panel measures as 0×0).
      if (!this.#isSheet()) this.#position();
      const first = this.panel?.querySelector(
        '[data-quick-pick-filter]:not([disabled]), [data-quick-pick-size]:not([disabled])'
      );
      first?.focus();
    } else {
      // Dismissing the picker returns the tile to its canonical cover (the stock
      // packshot) — the SAME reset the successful-add path uses (resetToCover).
      // The old code only reset when the card wasn't hovered, so on desktop
      // (cursor still over the tile) or touch (iOS sticky-hover) the tile lingered
      // on the hover/model image instead of the stock shot after "click away".
      // resetToCover ignores hover state + pointer type, so dismiss == add.
      const card = this.closest('product-card');
      if (card && typeof card.resetToCover === 'function') {
        card.resetToCover();
      } else if (card && typeof card.resetImage === 'function' && !card.matches(':hover')) {
        card.resetImage({ pointerType: 'mouse' });
      }
    }
  }

  /** Touch / no-hover devices show the picker as a viewport bottom sheet. */
  #isSheet() {
    return window.matchMedia('(hover: none)').matches;
  }

  /**
   * Carry the host's --sqp-accent onto the panel before it leaves this subtree.
   * The accent is set on the host and inherited by the chips; once the panel is
   * portaled to <body> it loses that inheritance — which would drop the chip
   * borders, the selected fill and the Add button's navy. Re-set it on the panel.
   */
  #carryAccent() {
    if (!this.panel) return;
    const accent = getComputedStyle(this).getPropertyValue('--sqp-accent').trim();
    if (accent) this.panel.style.setProperty('--sqp-accent', accent);
  }

  /**
   * Portal the panel onto <body> as a bottom sheet (touch). The card's media box
   * and the carousel track set container-type, making them the containing block
   * for fixed descendants — so an in-place fixed panel would still be clipped.
   * Moving it to <body> lets it anchor to the viewport and clear the clip.
   */
  #mountSheet() {
    if (this.#portaled) return;
    this.#backdrop = document.createElement('div');
    this.#backdrop.className = 'stuburt-card-quick-add__backdrop';
    this.#backdrop.addEventListener('click', () => this.#toggle(false));
    document.body.appendChild(this.#backdrop);
    if (this.panel) {
      this.#carryAccent();
      document.body.appendChild(this.panel);
      this.panel.classList.add('is-sheet');
    }
    document.body.style.overflow = 'hidden';
    this.#portaled = true;
  }

  /**
   * Portal the panel onto <body> as a floating popover (desktop). Same reason as
   * the sheet — escape the card-gallery's overflow:hidden (and every other
   * clipping/stacking ancestor) — but kept as the compact popover, free to extend
   * over neighbouring cards. #position anchors it to the trigger; no backdrop, it
   * closes on scroll / outside-click / Escape like the original in-card popover.
   */
  #mountFloating() {
    if (this.#portaled) return;
    if (this.panel) {
      this.#carryAccent();
      document.body.appendChild(this.panel);
      this.panel.classList.add('is-floating');
    }
    this.#portaled = true;
  }

  /**
   * Anchor the floating popover above the trigger, right edges aligned, clamped
   * into the viewport — so it can spill over the cards but never run off-screen.
   * Measures the panel after it's visible (offsetWidth/Height ignore transforms,
   * so the pop-in animation doesn't skew the math).
   */
  #position() {
    if (!this.panel || !this.panel.classList.contains('is-floating')) return;
    const margin = 8;
    const t = this.trigger.getBoundingClientRect();
    const pw = this.panel.offsetWidth;
    const ph = this.panel.offsetHeight;
    let left = t.right - pw;
    let top = t.top - margin - ph;
    left = Math.max(margin, Math.min(left, window.innerWidth - pw - margin));
    if (top < margin) top = margin;
    this.panel.style.left = `${left}px`;
    this.panel.style.top = `${top}px`;
  }

  /**
   * Tear down whichever portal is active: restore the panel into the control, drop
   * a sheet's backdrop, clear the carried accent + inline positioning, unlock scroll.
   */
  /**
   * Quantity stepper. The hidden [data-quick-pick-qty] input stays the single
   * source of truth — the add path already reads it — and this only clamps that
   * value and mirrors it into the visible number and the button states.
   *
   * The input lives on the component; the controls live in the panel, which
   * portals to <body>. Hence the two different lookup roots.
   */
  #onQtyStep = (event) => {
    const input = this.querySelector('[data-quick-pick-qty]');
    if (!input) return;
    const step = parseInt(event.currentTarget?.dataset.quickPickQtyStep, 10) || 0;
    const min = this.#qtyBase;
    const current = parseInt(input.value, 10) || min;
    input.value = String(Math.min(QTY_MAX, Math.max(min, current + step)));
    this.#syncQty();
  };

  #syncQty() {
    const input = this.querySelector('[data-quick-pick-qty]');
    if (!input) return;
    const min = this.#qtyBase;
    const value = Math.min(QTY_MAX, Math.max(min, parseInt(input.value, 10) || min));
    input.value = String(value);

    const label = this.panel?.querySelector('[data-quick-pick-qty-value]');
    if (label) label.textContent = String(value);

    for (const btn of this.panel?.querySelectorAll('[data-quick-pick-qty-step]') ?? []) {
      const step = parseInt(btn.dataset.quickPickQtyStep, 10) || 0;
      btn.disabled = step < 0 ? value <= min : value >= QTY_MAX;
    }
  }

  /**
   * Play the exit animation, then hide and unmount. The panel keeps its portal
   * and its .is-sheet/.is-floating classes for the duration — #unmount strips
   * both, so calling it first is what made the sheet disappear instead of
   * sliding back down.
   *
   * animationend is the primary signal, with a timeout backstop: if the
   * animation is suppressed (reduced motion, a browser that skips it, the
   * element already off-screen) the event never fires, and without the fallback
   * the panel would stay mounted and the page stay scroll-locked.
   */
  #playClose() {
    const panel = this.panel;
    const backdrop = this.#backdrop;

    const finish = () => {
      panel.classList.remove('is-closing');
      backdrop?.classList.remove('is-closing');
      panel.hidden = true;
      this.#unmount();
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    panel.classList.add('is-closing');
    backdrop?.classList.add('is-closing');

    let settled = false;
    const once = (event) => {
      // Ignore bubbling animationend from chips etc. inside the panel.
      if (event && event.target !== panel) return;
      if (settled) return;
      settled = true;
      panel.removeEventListener('animationend', once);
      finish();
    };
    panel.addEventListener('animationend', once);
    window.setTimeout(once, 400);
  }

  #unmount() {
    if (!this.#portaled) return;
    if (this.panel) {
      this.panel.classList.remove('is-sheet', 'is-floating');
      this.panel.style.removeProperty('--sqp-accent');
      this.panel.style.removeProperty('left');
      this.panel.style.removeProperty('top');
      this.appendChild(this.panel);
    }
    if (this.#backdrop) {
      this.#backdrop.remove();
      this.#backdrop = null;
    }
    document.body.style.overflow = '';
    this.#portaled = false;
  }

  async #add(variantId) {
    if (!variantId || this.#busy) return;
    this.#busy = true;
    // Optimistic feedback: play the tick the INSTANT the add is pressed, not after
    // the /cart/add.js round-trip — the add nearly always succeeds, and waiting on
    // the network made the animation feel laggy. The catch below reverts to the
    // error shake on the rare failure.
    this.trigger.classList.remove('is-error');
    this.trigger.classList.add('is-added');

    const qtyInput = this.querySelector('[data-quick-pick-qty]');
    const body = new FormData();
    body.set('id', variantId);
    body.set('quantity', qtyInput?.value || '1');
    // Ask Horizon to re-render the cart bubble AND the cart drawer's line items in
    // the same round trip. CUSTOM (Stuburt, 2026-08-23): the bubble alone left the
    // drawer nothing to morph, so it had to fire a second network render of the
    // header before it could open, and on a cold cart that wait outran its deadline
    // and the sheet never appeared. Helper + fallback: assets/cart-drawer.js.
    body.append('sections', window.stuburtCartAddSections ? window.stuburtCartAddSections() : 'cart-icon-bubble');
    body.append('sections_url', window.location.pathname);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body,
        // No X-Requested-With: the store's bot protection answers cart adds carrying
        // AJAX markers with a fake 422 "sold out" (see error ledger 2026-07-05).
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('add failed');
      const data = await res.json().catch(() => ({}));

      // Pass the card image + chosen options so the mini-cart can render the
      // correct thumbnail instantly. Prefer the card's ALREADY-LOADED image
      // (currentSrc) — it's in cache, so the popup shows it with no extra fetch.
      const match = this.#matrix.find((x) => String(x.id) === String(variantId));
      const variantText = match ? [match.f, match.v].filter(Boolean).join(' · ') : '';
      const cardImg = this.closest('.card-gallery')?.querySelector('img');
      const image = cardImg?.currentSrc || cardImg?.src || this.dataset.image || '';

      // CUSTOM (Stuburt, wardrobe lander): a carousel that opts in via
      // data-quick-pick-advance (carousel-p setting) walks the customer to the next
      // chapter of the wardrobe story after a successful add. Resolved BEFORE the
      // dispatch (2026-08-23) because the cart sheet has to know whether a walk is
      // coming — opening now would scroll-lock the body part-way through it. Without
      // the attribute, or with no chapter left after this carousel, all a no-op.
      const advHost = this.closest('[data-quick-pick-advance]');
      let advTarget = null;
      if (advHost) {
        const stops = document.querySelectorAll(
          '.section-stuburt-wardrobe-chapter, .section-stuburt-wardrobe-cta'
        );
        for (const el of stops) {
          if (advHost.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) {
            advTarget = el;
            break;
          }
        }
      }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Horizon's cart bubble and the cart drawer's line items both subscribe to
      // this. The drawer morphs its contents off this event either way; deferDrawer
      // holds back only the OPEN, so the sheet is already filled by the time the
      // walk below finishes and openWhenReady() is called.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              source: 'product-form-component',
              itemCount: 1,
              variantId: String(variantId),
              sections: data.sections,
              deferDrawer: Boolean(advTarget),
              product: {
                image,
                title: this.dataset.title || '',
                variant: variantText,
              },
            },
          },
        })
      );

      // CUSTOM (Stuburt): snap the card back to its packshot after a successful
      // add. On touch the gallery's hover reset never fires (previewImage/
      // resetImage bail on non-mouse pointers, and iOS sticky-hover can defeat the
      // picker's own `!:hover` reset guard), so the tile could otherwise linger on
      // a previewed image. resetToCover ignores pointer type + hover state.
      const card = this.closest('product-card');
      if (card && typeof card.resetToCover === 'function') card.resetToCover();

      // The picker panel is already closed by this point (#toggle(false) runs before
      // #add), so give the tick a beat to read, walk to the chapter resolved above,
      // and only once that has settled slide the cart open. Strictly one thing at a
      // time — the same contract the quick-shop sheet follows.
      //
      // Fixed delays rather than a scroll-end listener: every other beat in this
      // file is timed the same way, and `scrollend` is too new to lean on across
      // the iOS versions this store sees. The open is late rather than early on
      // purpose — arriving mid-scroll strands the page, arriving a beat late reads
      // as deliberate.
      if (advTarget) {
        const scrollDelay = reducedMotion ? 0 : 650;
        window.setTimeout(() => {
          advTarget.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        }, scrollDelay);
        window.setTimeout(() => {
          document.querySelector('cart-drawer-component')?.openWhenReady?.();
        }, scrollDelay + (reducedMotion ? 150 : 800));
      }

      window.setTimeout(() => {
        this.trigger.classList.remove('is-added');
        this.#busy = false;
      }, 1400);
    } catch {
      // Add failed — pull the optimistic tick back and shake instead.
      this.trigger.classList.remove('is-added');
      this.trigger.classList.add('is-error');
      window.setTimeout(() => {
        this.trigger.classList.remove('is-error');
        this.#busy = false;
      }, 1300);
    }
  }
}

if (!customElements.get('stuburt-quick-pick')) {
  customElements.define('stuburt-quick-pick', StuburtQuickPick);
}
